<template>
  <!-- Page Content -->
  <div class="whole-page" id="whole-page-div">
    <AIChatFloat :colorTheme="colorTheme" />
    <div class="navbar-div">
      <NavbarComponent
        :user="user"
        :colorTheme="colorTheme"
        analysis="DATA"
        :selectedNavLink="selectedNavLink"
        @eventOnUserSelectNavLink="eventOnClientSelect"
        @eventOnUserColorTheme="onUserColorTheme"
      />
    </div>
    <div class="h-100 d-flex flex-column">
      <!-- Link 0 selected / Currency -->
      <div class="row f-row h-100" v-if="selectedNavLink === 0">
        <div class="col col-12 h-100">
          <Currency
            :companies="companies"
            :colorTheme="colorTheme"
            :currencySpecs="currencySpecs"
            :processData="processData"
            :selectedNavAsset="selectedNavLink"
            :selectedSheet="selectedSheet"
            :user="user"
            @eventOnClientSelectSheet="onClientSelectSheet"
          />
        </div>
      </div>
      <!-- Link 1 selected / Commodity -->
      <div class="row f-row h-100" v-if="selectedNavLink === 1">
        <div class="col col-12 h-100">
          <Commodity
            :companies="companies"
            :colorTheme="colorTheme"
            :commoditySpecs="commoditySpecs"
            :processData="processData"
            :selectedSheet="selectedSheet"
            :selectedNavAsset="selectedNavLink"
            :user="user"
            @eventOnClientSelectSheet="onClientSelectSheet"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIChatFloat from "@/components/ai/AIChatFloat.vue";
import Commodity from "@/components/dataModel/Commodity";
import Currency from "@/components/dataModel/Currency";
import NavbarComponent from "@/components/Navbar.vue";
import {
  assetSelectionVuex,
  colorThemeChange,
  colorThemeVuex,
  sortByKey,
} from "@/_core/_helpers/_helpers";
import { ProcessData } from "@/_core/data/ProcessData";
import { saveUserPreferences } from "@/_core/data/SaveToDB";

const PRODUCT_TYPES = ["currency", "commodity"];
const SUBVIEW_SLUGS = ["exposures", "hedges", "hedging-program", "upload"];
const SUBVIEW_NAME_TO_INDEX = {
  "data-exposures": 0,
  "data-hedges": 1,
  "data-hedging-program": 2,
  "data-upload": 3,
};

export default {
  name: "DataModel",
  components: {
    AIChatFloat,
    Currency,
    Commodity,
    NavbarComponent,
  },
  props: {
    productType: { type: String, default: null },
  },
  data() {
    return {
      dataModelPreferences: {},
      colorTheme: true, // true = dark, else light
      commoditySpecs: [],
      companies: [{ id: 0, company_name: "N.A." }],
      selectedSheet: 0,
      currencySpecs: [],
      linkList: ["Currencies", "Commodities"],
      processData: null,
      root: document.documentElement,
      selectedNavLink: 0,
      user: [],
    };
  },
  created() {
    this.dataModelPreferences = this.$store.state.user_preferences.all.items.datamodel_choices;
    try {
      this.colorTheme = this.dataModelPreferences.color_theme;
      if (this.colorTheme === undefined) {
        this.colorTheme = true;
      }
      if (!this.colorTheme) {
        colorThemeChange(this.root, this.colorTheme);
        this.onUserColorTheme(this.colorTheme);
      }
    } catch (error) {
      console.log(`Error color theme ${error}`);
    }
    this.processData = new ProcessData();
    try {
      this.selectedNavLink = this.dataModelPreferences.selected_nav;
    } catch (error) {
      this.selectedNavLink = 0;
      console.log(`Error selected nav link ${error}`);
    }
    try {
      this.selectedSheet = this.dataModelPreferences.selected_sheet;
    } catch (error) {
      this.selectedSheet = 0;
      console.log(`Error selected custom nav asset ${error}`);
    }
    try {
      this.user = this.$store.state.user.all.items;
    } catch (error) {
      console.log(`Error user ${error}`);
    }
    try {
      this.companies = this.$store.state.companies.all.items;
      this.companies = sortByKey(this.companies, "company_name");
      this.companies.forEach(function(d, i) {
        d.id = i;
      });
      let sel_comp_error = false;
      if (
        this.$store.state.user_preferences.all.items.analytics_choices
          .selected_company === undefined
      ) {
        sel_comp_error = true;
      } else if (
        this.$store.state.user_preferences.all.items.analytics_choices
          .selected_company.length === 0
      ) {
        sel_comp_error = true;
      }
      if (sel_comp_error) {
        this.$store.state.user_preferences.all.items.analytics_choices.selected_company = [
          this.companies[0],
        ];
        saveUserPreferences(this);
      }
    } catch (error) {
      console.log(`Error companies ${error}`);
    }
    try {
      this.currencySpecs = this.$store.state.currencies.all.items;
    } catch (error) {
      console.log(`Error currencies ${error}`);
    }
    try {
      this.commoditySpecs = this.$store.state.commodities.all.items;
    } catch (error) {
      console.log(`Error commodities ${error}`);
    }

    this.applyRouteToState(this.$route);
    this.$nextTick(() => this.syncDataPath(true));

    // document.title = "Data Model | Quanspire";
  },
  watch: {
    $route(to) {
      this.applyRouteToState(to);
    },
  },
  methods: {
    applyRouteToState(route) {
      const navIndex = PRODUCT_TYPES.indexOf(route.params.productType);
      if (navIndex > -1 && navIndex !== this.selectedNavLink) {
        this.selectedNavLink = navIndex;
      }
      const sheetIndex = SUBVIEW_NAME_TO_INDEX[route.name];
      if (sheetIndex != null && sheetIndex !== this.selectedSheet) {
        this.selectedSheet = sheetIndex;
      }
    },
    buildDataPath() {
      return {
        name: "data-" + SUBVIEW_SLUGS[this.selectedSheet],
        params: { productType: PRODUCT_TYPES[this.selectedNavLink] },
        query: { ...this.$route.query },
      };
    },
    isSameDataPath(target) {
      if (this.$route.name !== target.name) return false;
      const current = this.$route.params.productType || null;
      return current === (target.params.productType || null);
    },
    syncDataPath(replace = false) {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      const target = this.buildDataPath();
      if (this.isSameDataPath(target)) return;
      const navigation = replace
        ? this.$router.replace(target)
        : this.$router.push(target);
      navigation.catch((err) => {
        if (err && err.name !== "NavigationDuplicated") {
          console.error("Error updating data-model path:", err);
        }
      });
    },
    onUserColorTheme(val) {
      this.colorTheme = val;
      this.dataModelPreferences.color_theme = val;
      colorThemeVuex(this, this.colorTheme);
      saveUserPreferences(this);
    },
    eventOnClientSelect(idx) {
      this.dataModelPreferences.selected_nav = idx;
      assetSelectionVuex(this, idx);
      this.$store.state.user_preferences.all.items.analytics_choices = this.dataModelPreferences;
      saveUserPreferences(this);
      this.selectedNavLink = idx;
      this.syncDataPath();
    },
    onClientSelectSheet(idx) {
      this.selectedSheet = idx;
      this.dataModelPreferences.selected_sheet = idx;
      this.$store.state.user_preferences.all.items.datamodel_choices = this.dataModelPreferences;
      saveUserPreferences(this);
      this.syncDataPath();
    },
  },
};
</script>

<style scoped></style>
